import React, { useContext, useEffect, useState, useRef } from "react";

import { uniqBy } from "lodash";
import styled, { useTheme } from "styled-components";
import debounce from "lodash.debounce";
import { nanoid } from "nanoid";
import SearchIcon from "@atlaskit/icon/glyph/editor/search";
import CheckIcon from "@atlaskit/icon/glyph/check";
import Textfield from "@atlaskit/textfield";
import Select from "@atlaskit/select";
import Spinner from "@atlaskit/spinner";

import SimpleBar from "../../Simplebar";
import {
  ImageAttribution,
  ImageThumbnail,
  ShowMoreButton,
  MasonryContainer,
  Label,
  ScrollContainer,
} from "../styled/StyledComponents";
import UnsplashLogo from "../Icons/Unsplash";
import { UnsplashService } from "../RestService";
import { BackgroundDialogContext } from "../Context";
import { colorOptions } from "../UnsplashOptions";
import { IMAGE_TYPES } from "../ImageSourceTypes";
import { getLocalStorage, setLocalStorage } from "../storageService";
import { Landscape, Portrait, Square } from "../Icons/OrientationIcons";
import NoResults from "../NoResults";
import { useOpticsStore } from "../../../../../stores/use-optics-store";
import { useTranslation } from "react-i18next";

export const TextFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  margin-top: -10px;

  & > div {
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 17px !important;
    height: 40px !important;
    color: ${({ theme }) => theme.text.input} !important;
    border-color: ${({ theme }) => theme.bg.primary} !important;
    background: ${({ theme }) => theme.bg.primary};
  }

  svg {
    color: ${({ theme }) => theme.text.text} !important;
  }
`;

export const SelectContainer = styled.div`
  flex: 1;
  font-size: 14px;

  div[class*="control"] {
    border-color: ${({ theme }) => theme.bg.primary} !important;
    background: ${({ theme }) => theme.bg.primary};
  }
  div[class*="menu"] {
    margin-left: 2px;
  }
`;

const UnsplashFiltersWrapper = styled.div`
  margin: 16px 0px 24px 0px;
  display: flex;
  align-items: center;
  column-gap: 16px;
`;

const OrientationWrapper = styled.div`
  padding: 12px 13px;
  display: flex;
  align-items: center;
  column-gap: 12px;
  background: ${({ theme }) => theme.bg.primary};
  border-radius: 3px;
  height: 40px;
  box-sizing: border-box;
`;

const LineDivider = styled.div`
  height: 100%;
  border-right: 1px solid ${({ border }) => border};
`;

const IconContainer = styled.div`
  height: 100%;
  width: auto;
  display: grid;
  place-content: center;
  cursor: pointer;
`;

const DEFAULT_COLUMNS = [
  { images: [], size: 0 },
  { images: [], size: 0 },
  { images: [], size: 0 },
];

function Unsplash({ imageOrientation = {}, imageColor = {} }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [recent] = useState(getLocalStorage("unsplash"));

  const { orientation, setOrientation } = imageOrientation;
  const { color, setColor } = imageColor;
  const theme = useTheme();

  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");

  const { image, setImage, isMulti } = useContext(BackgroundDialogContext);

  const { unsplashSearch, setUnsplashSearch } = useOpticsStore((state) => ({
    unsplashSearch: state.unsplashSearch,
    setUnsplashSearch: state.setUnsplashSearch,
  }));

  let [col1, col2, col3] = [...columns];

  const addImagesToColumns = (images, array) => {
    let cols = [...array];

    images.forEach((img) => {
      let smallestCol = 0;

      if (cols[1].size <= cols[0].size && cols[1].size <= cols[2].size) {
        smallestCol = 1;
      }
      if (cols[2].size <= cols[0].size && cols[2].size <= cols[1].size) {
        smallestCol = 2;
      }

      cols[smallestCol] = {
        images: [...cols[smallestCol].images, img],
        size: cols[smallestCol].size + img.height / img.width,
      };
    });

    setColumns(cols);
  };

  const searchImages = useRef(
    debounce((callback) => {
      callback().then(({ data }) => {
        addImagesToColumns(data.results, DEFAULT_COLUMNS);
        setHasMore(data.total_pages > 1);
        setIsLoading(false);
      });
    }, 500)
  ).current;

  useEffect(() => {
    UnsplashService.searchImages(
      unsplashSearch || "city",
      page,
      color,
      orientation
    ).then(({ data }) => addImagesToColumns(data.results, columns));
  }, []);

  useEffect(() => {
    setPage(1);
    setIsLoading(true);
    searchImages(() =>
      UnsplashService.searchImages(
        unsplashSearch || search,
        1,
        color,
        orientation
      )
    );
  }, [unsplashSearch, search, color, orientation]);

  const showImage = (img) => (
    <ImageThumbnail
      key={nanoid(16)}
      height={`${(img.height / img.width) * 120.5}px`}
      isSelected={
        isMulti ? image.find(({ id }) => id === img.id) : image.id === img.id
      }
      onClick={() => {
        UnsplashService.trackDownload(img.links.download_location);

        setLocalStorage("unsplash", [img, ...recent]);

        if (isMulti) {
          if (image.find(({ id }) => id === img.id)) {
            return setImage([...image.filter(({ id }) => id !== img.id)]);
          }

          return setImage([
            ...image,
            {
              source: IMAGE_TYPES.UNSPLASH,
              link: img.urls.regular,
              id: img.id,
            },
          ]);
        }
        return setImage({
          source: IMAGE_TYPES.UNSPLASH,
          link: img.urls.regular,
          id: img.id,
        });
      }}
    >
      <img src={img.urls.thumb} alt={img.description} />

      <CheckIcon size="small" />

      <ImageAttribution
        href={`${img.user.links.html}?utm_source=Caelor%20-%20Page%20Builder&utm_medium=referral`}
        target="_blank"
      >
        <img src={img.user.profile_image.small} alt={img.user.username} />
        <span>{img.user.username}</span>
      </ImageAttribution>
    </ImageThumbnail>
  );

  return (
    <>
      <TextFieldContainer>
        <Textfield
          elemAfterInput={
            <SearchIcon size="large" primaryColor={theme.global.text.dark} />
          }
          placeholder={t("search-images")}
          value={unsplashSearch}
          onChange={(e) => {
            const { value } = e.target;
            window.unsplashSearch = value;

            if (value === window.unsplashSearch) {
              setSearch(value);
              if (setUnsplashSearch) {
                setUnsplashSearch(value);
              }
            }
          }}
        />
      </TextFieldContainer>

      <UnsplashFiltersWrapper>
        <SelectContainer>
          <Select
            value={color}
            options={colorOptions}
            onChange={setColor}
            placeholder={t("search-by-color")}
            isClearable
          />
        </SelectContainer>

        <OrientationWrapper>
          <IconContainer
            onClick={() => {
              if (orientation === "landscape") {
                setOrientation(undefined);
              } else {
                setOrientation("landscape");
              }
            }}
          >
            <Landscape isActive={orientation === "landscape"} />
          </IconContainer>

          <LineDivider border={theme.shared.sidebar.divider} />

          <IconContainer
            onClick={() => {
              if (orientation === "portrait") {
                setOrientation(undefined);
              } else {
                setOrientation("portrait");
              }
            }}
          >
            <Portrait isActive={orientation === "portrait"} />
          </IconContainer>

          <LineDivider border={theme.shared.sidebar.divider} />

          <IconContainer
            onClick={() => {
              if (orientation === "squarish") {
                setOrientation(undefined);
              } else {
                setOrientation("squarish");
              }
            }}
          >
            <Square isActive={orientation === "squarish"} />
          </IconContainer>
        </OrientationWrapper>
      </UnsplashFiltersWrapper>

      <ScrollContainer>
        <SimpleBar
          style={{
            position: "absolute",
            width: "378px",
            height: "calc(100% - 270px)",
            paddingRight: "15px",
          }}
        >
          {!col1.size && !col2.size && !col3.size && !isLoading ? (
            <p style={{ textAlign: "center" }}>
              <NoResults
                label={t("no-results")}
                content={
                  search
                    ? `${t("no-results-found-for")} "${search}", ${t(
                        "please-try-searching-something-else"
                      )}`
                    : ""
                }
              />
            </p>
          ) : (
            <>
              {!recent.length || (
                <>
                  <Label>{t("recently-used")}</Label>
                  <MasonryContainer style={{ marginBottom: "20px" }}>
                    {uniqBy(recent, "id").map((img) => (
                      <div>{showImage({ ...img, width: 120, height: 90 })}</div>
                    ))}
                  </MasonryContainer>
                </>
              )}

              {!isLoading && (
                <>
                  <Label>{search ? t("search-results") : t("all")}</Label>
                  <MasonryContainer>
                    <div>{col1.images.map(showImage)}</div>
                    <div>{col2.images.map(showImage)}</div>
                    <div>{col3.images.map(showImage)}</div>
                  </MasonryContainer>
                </>
              )}
            </>
          )}

          {isLoading && (
            <ShowMoreButton style={{ marginTop: "70px" }}>
              <Spinner size="large" />
            </ShowMoreButton>
          )}

          {hasMore && !isLoading && (
            <ShowMoreButton
              onClick={() => {
                setIsLoading(true);

                UnsplashService.searchImages(
                  unsplashSearch || search,
                  page + 1,
                  color,
                  orientation
                ).then(({ data }) => {
                  setPage(page + 1);
                  addImagesToColumns(data.results, columns);
                  setHasMore(data.total_pages > page);
                  setIsLoading(false);
                });
              }}
            >
              {t("show-more")}
            </ShowMoreButton>
          )}
        </SimpleBar>
      </ScrollContainer>

      <a href="https://unsplash.com/" target="_blank" rel="noreferrer">
        <UnsplashLogo />
      </a>
    </>
  );
}

export default Unsplash;
